/**
 * Signed-in shopper's order history — reachable at /my-orders. Renders every
 * order matching the shopper's email address (newest first) with a summary of
 * the line items, subtotal, discount and total.
 */
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

import PageShell from "@/components/PageShell";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

function fmtMoney(amount, currency = "GBP") {
  const symbol = { GBP: "£", USD: "$", EUR: "€" }[currency] || "";
  return `${symbol}${Number(amount || 0).toFixed(2)}`;
}

function OrderCard({ order }) {
  const status = order.payment_status || order.status || "pending";
  const badgeCls = status === "paid"
    ? "bg-green-100 text-green-700"
    : status === "failed"
      ? "bg-red-100 text-red-700"
      : "bg-amber-100 text-amber-700";
  return (
    <Link
      to={`/my-orders/${order.order_id}`}
      className="block border border-border rounded-lg bg-background p-4 sm:p-6 hover:border-primary hover:shadow-sm transition-all"
      data-testid={`my-order-card-${order.order_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <div className="text-xs text-muted-foreground">Order #{(order.order_id || "").slice(0, 8).toUpperCase()}</div>
          <div className="text-sm">{new Date(order.created_at).toLocaleString()}</div>
        </div>
        <span className={`inline-block text-xs px-2 py-0.5 rounded ${badgeCls} capitalize self-start`}>
          {status}
        </span>
      </div>
      <ul className="divide-y divide-border">
        {(order.items || []).map((it, idx) => (
          <li key={idx} className="flex items-center gap-3 py-2">
            {it.image_url ? (
              <img
                src={it.image_url}
                alt=""
                className="h-12 w-12 rounded object-cover border"
              />
            ) : (
              <div className="h-12 w-12 rounded bg-muted/40 border" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{it.name}</div>
              <div className="text-xs text-muted-foreground">Qty {it.quantity} · {fmtMoney(it.price, order.currency)}</div>
            </div>
            <div className="text-sm font-medium whitespace-nowrap">
              {fmtMoney(Number(it.price || 0) * Number(it.quantity || 1), order.currency)}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-3 border-t border-border text-sm space-y-1">
        {order.discount_amount > 0 && (
          <div className="flex justify-between text-green-700">
            <span>Discount{order.discount_code ? ` (${order.discount_code})` : ""}</span>
            <span>-{fmtMoney(order.discount_amount, order.currency)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold">
          <span>Total</span>
          <span>{fmtMoney(order.total, order.currency)}</span>
        </div>
      </div>
    </Link>
  );
}

export default function MyOrdersPage() {
  const { shopper, loading: authLoading, signIn } = useShopperAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!shopper) { setLoading(false); return; }
    let alive = true;
    axios.get(`${API}/shopper/me/orders`, { withCredentials: true })
      .then(({ data }) => { if (alive) setOrders(data.orders || []); })
      .catch(() => { if (alive) toast.error("Failed to load your orders"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [shopper, authLoading]);

  return (
    <PageShell chrome>
      <main className="max-w-3xl mx-auto px-4 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="my-orders-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <h1 className="text-3xl font-serif mb-2 flex items-center gap-2">
          <ShoppingBag className="h-6 w-6" /> My orders
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          A complete history of everything you&apos;ve bought — receipts are emailed to you automatically after each order.
        </p>

        {authLoading || loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !shopper ? (
          <div className="border border-dashed border-border rounded-lg p-8 text-center bg-muted/20" data-testid="my-orders-signin-gate">
            <p className="text-sm text-muted-foreground mb-4">Sign in with Google to see your order history.</p>
            <button
              type="button"
              onClick={() => signIn()}
              className="inline-flex items-center gap-2 h-10 px-4 border border-border rounded-md text-sm font-medium bg-background hover:bg-muted/50 transition-colors"
              data-testid="my-orders-signin-btn"
            >
              Sign in with Google
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-8 text-center bg-muted/20" data-testid="my-orders-empty">
            <p className="text-sm text-muted-foreground">You haven&apos;t placed any orders yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="my-orders-list">
            {orders.map((o) => <OrderCard key={o.order_id} order={o} />)}
          </div>
        )}
      </main>
    </PageShell>
  );
}

// Consumers can also `import { RequireShopper }` if they want a stricter guard.
export function RequireShopper({ children }) {
  const { shopper, loading } = useShopperAuth();
  if (loading) return null;
  if (!shopper) return <Navigate to="/" replace />;
  return children;
}
